import AppConstants from '../constants/app-constants';
import AppDispatcher from '../dispatchers/app-dispatcher';
import LoginApi from '../api/login-api';
import ArtifactsApi from '../api/artifacts-api';
import DeploymentsApi from '../api/deployments-api';
import DevicesApi from '../api/devices-api';
import GeneralApi from '../api/general-api';
import UsersApi from '../api/users-api';
import PreferencesApi from '../api/preferences-api';
import InfraStructureApi from '../api/infrastructure-api';
import parse from 'parse-link-header';
import { advanceOnboarding } from '../utils/onboardingmanager';

import AppStore from '../stores/app-store';

import { convertDbToMultiTenancy } from '../helpers';
import CamerasApi from '../api/cameras-api';
import EventsAndMetricsApi from '../api/eventsAndMetrics-api';

var rootUrl = 'https://localhost:443';
const apiUrl = `${rootUrl}/api/management/v1`;
const apiUrlV2 = `${rootUrl}/api/management/v2`;
const deploymentsApiUrl = `${apiUrl}/deployments`;
const aiDeploymentsApiUrl = `${apiUrl}/aideployments`;
const deviceAuthV2 = `${apiUrlV2}/devauth`;
const inventoryApiUrl = `${apiUrl}/inventory`;
const useradmApiUrl = `${apiUrl}/useradm`;
const tenantadmUrl = `${apiUrl}/tenantadm`;
const tenantadmUrlV2 = `${apiUrlV2}/tenantadm`;
const hostedLinks = 'https://s3.amazonaws.com/hosted-mender-artifacts-onboarding/';

const configurationApiUrl = `${apiUrl}/configurations`;
const configurationList = '/list';
const configurationCount = `${configurationList}/count`;

const localConfigurationUrl = `${apiUrl}/localconf/lconf`;

const actionApiUrl = `${apiUrl}/action`;
const influxdbUrl = `${apiUrl}/influxdb`;
const preferencesUrl = `${apiUrl}/preferences`;
const infrastructureUrl = `${apiUrlV2}/infra_manager/infrastructures`;
const vcamlogicUrl = `${apiUrl}/vcamlogic`;
const vcamlogicUrlV2 = `${apiUrlV2}/vcamlogic`;
const notificationMgr = `${apiUrl}/notification_mgr`;
// default per page until pagination and counting integrated
const default_per_page = 20;
const default_page = 1;

const AppActions = {
  /*
   * Device inventory functions
   */
  selectGroup: group => {
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SELECT_GROUP,
      group: group
    });
  },

  selectDevice: device =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SELECT_DEVICE,
      device
    }),

  addDeviceToGroup: (group, device) => {
    return DevicesApi.put(`${inventoryApiUrl}/devices/${device}/group`, { group });
  },

  removeDeviceFromGroup: (device, group) => {
    return DevicesApi.delete(`${inventoryApiUrl}/devices/${device}/group/${group}`);
  },

  addGroup: (group, idx) => {
    AppDispatcher.handleViewAction({
      actionType: AppConstants.ADD_GROUP,
      group: group,
      index: idx
    });
  },

  /* Groups */
  getGroups: () =>
    DevicesApi.get(`${inventoryApiUrl}/groups`).then(res => {
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_GROUPS,
        groups: res.body
      });
      return Promise.resolve(res.body);
    }),

  getGroupDevices: (group, page = default_page, per_page = default_per_page) => {
    var forGroup = group ? `&group=${group}` : '&has_group=false';
    return DevicesApi.get(`${inventoryApiUrl}/devices?per_page=${per_page}&page=${page}${forGroup}`);
  },

  setGroupDevices: devices => {
    AppDispatcher.handleViewAction({
      actionType: AppConstants.RECEIVE_GROUP_DEVICES,
      devices: devices
    });
  },

  setFilterAttributes: attrs =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_FILTER_ATTRIBUTES,
      attrs: attrs
    }),

  getDeviceById: id => DevicesApi.get(`${inventoryApiUrl}/devices/${id}`).then(res => res.body),

  getDevicesWithInventory: devices =>
    Promise.all(
      devices.map(device => {
        // have to call inventory each time - accepted list can change order so must refresh inventory too
        return AppActions.getDeviceById(device.id).then(inventory => {
          device.attributes = inventory.attributes;
          device.updated_ts = inventory.updated_ts;
          return Promise.resolve(device);
        });
      })
    ),

  getDevices: (page = default_page, per_page = default_per_page, search_term) => {
    var orderObject = AppStore.getSortingOrder();
    var order = '';
    //sort=attr_name1:asc or  sort=attr_name1:desc
    if (orderObject.UUID == true) {
      order = 'asc'
    } else {
      order = 'desc'
    }

    var sort_param = order ? `sort=UUID:${order}` : '';

    // get devices from inventory
    var search = search_term ? `&${search_term}` : '';
    return DevicesApi.get(`${inventoryApiUrl}/devices?per_page=${per_page}&page=${page}${search}&${sort_param}`).then(res => {
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_ALL_DEVICES,
        devices: res.body
      });
      return res.body;
    });
  },
  getAllDevices: () => {
    const getAllDevices = (per_page = 200, page = 1, devices = []) =>
      DevicesApi.get(`${inventoryApiUrl}/devices?per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        devices.push(...res.body);
        if (links.next) {
          return getAllDevices(per_page, page + 1, devices);
        }
        return Promise.resolve(devices);
      });
    return getAllDevices();
  },
  getNumberOfDevicesInGroup: function (group) {
    var forGroup = group ? `&group=${group}` : '&has_group=false';
    return DevicesApi.get(`${inventoryApiUrl}/devices?per_page=1&page=1${forGroup}`).then(res => Promise.resolve(Number(res.headers['x-total-count'])));
  },
  getAllDevicesInGroup: function (group) {
    var forGroup = group ? `&group=${group}` : '&has_group=false';
    const getDeviceCount = (per_page = 200, page = 1, devices = []) =>
      DevicesApi.get(`${inventoryApiUrl}/devices?per_page=${per_page}&page=${page}${forGroup}`).then(function (res) {
        var links = parse(res.headers['link']);
        devices.push(...res.body);
        if (links.next) {
          return getDeviceCount(per_page, page + 1, devices);
        }
        return Promise.resolve(devices);
      });
    return getDeviceCount();
  },

  /*
    Device Auth + admission
  */

  getDeviceCount: status => {
    var filter = status ? `?status=${status}` : '';

    return DevicesApi.get(`${deviceAuthV2}/devices/count${filter}`).then(res => {
      switch (status) {
      case 'pending':
        AppDispatcher.handleViewAction({
          actionType: AppConstants.SET_PENDING_DEVICES_COUNT,
          count: res.body.count
        });
        break;
      case 'accepted':
        AppDispatcher.handleViewAction({
          actionType: AppConstants.SET_ACCEPTED_DEVICES_COUNT,
          count: res.body.count
        });
        break;
      case 'rejected':
        AppDispatcher.handleViewAction({
          actionType: AppConstants.SET_REJECTED_DEVICES_COUNT,
          count: res.body.count
        });
        break;
      case 'preauthorized':
        AppDispatcher.handleViewAction({
          actionType: AppConstants.SET_PREAUTH_DEVICES_COUNT,
          count: res.body.count
        });
        break;
      default:
        AppDispatcher.handleViewAction({
          actionType: AppConstants.SET_TOTAL_DEVICES,
          count: res.body.count
        });
      }
      return Promise.resolve(res.body.count);
    });
  },

  getDeviceLimit: () =>
    DevicesApi.get(`${deviceAuthV2}/limits/max_devices`).then(res => {
      AppDispatcher.handleViewAction({
        actionType: AppConstants.SET_DEVICE_LIMIT,
        limit: res.body.limit
      });
      return Promise.resolve(res.body.limit);
    }),

  getDevicesByStatus: (status, page = default_page, per_page = default_per_page) => {
    var order = AppStore.getSortingOrder();
    var sort_order = order ? `id_data=${order.UUID}` : '';

    var dev_status = status ? `status=${status}` : '';
    return DevicesApi.get(`${deviceAuthV2}/devices?${dev_status}&per_page=${per_page}&page=${page}&${sort_order}`).then(response => {
      if (status) {
        const constant = `SET_${status.toUpperCase()}_DEVICES`;
        AppDispatcher.handleViewAction({
          actionType: AppConstants[constant],
          devices: response.body
        });
      }
      return Promise.resolve(response.body);
    });
  },

  getAllDevicesByStatus: status => {
    const getAllDevices = (per_page = 200, page = 1, devices = []) =>
      DevicesApi.get(`${deviceAuthV2}/devices?status=${status}&per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        devices.push(...res.body);
        if (links.next) {
          return getAllDevices(per_page, page + 1, devices);
        }
        return Promise.resolve(devices);
      });
    return getAllDevices();
  },

  getDeviceAuth: id => DevicesApi.get(`${deviceAuthV2}/devices/${id}`).then(res => res.body),

  updateDeviceAuth: (device_id, auth_id, status, type, flavor_name, config_name, label = '') =>
    DevicesApi.put(`${deviceAuthV2}/devices/${device_id}/auth/${auth_id}/status`,
      {
        status: status,
        type: type,
        flavor_name: flavor_name,
        config_name: config_name,
        unique_devparams: '',  //For future
        label: label
      }
    ),

  deleteAuthset: (device_id, auth_id) => DevicesApi.delete(`${deviceAuthV2}/devices/${device_id}/auth/${auth_id}`),

  preauthDevice: authset => {
    console.log(authset);
    return DevicesApi.post(`${deviceAuthV2}/devices`, authset);
  },

  decommissionDevice: id => DevicesApi.delete(`${deviceAuthV2}/devices/${id}`),

  /*
    General
  */
  setSnackbar: (message, duration, action, component, onClick, onClose) =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SNACKBAR,
      message: message,
      duration: duration,
      action: action,
      children: component,
      onClick: onClick,
      onClose: onClose
    }),

  /*
    User management
  */
  loginUser: (userData, data) =>
    LoginApi.post(`${useradmApiUrl}/auth/login`, userData, data)
      .then(res => res.text)
      .catch(err => {
        if (err.error.code && err.error.code !== 200) {
          return Promise.reject(err);
        }
      }),

  forgotpwd: data => UsersApi.post(`${useradmApiUrl}/auth/forgotpwd?email=${data.email}`),

  resetPassword: data => UsersApi.post(`${useradmApiUrl}/auth/reset`, data),

  getUserList: () => UsersApi.get(`${useradmApiUrl}/users`),

  getUser: id => UsersApi.get(`${useradmApiUrl}/users/${id}`),

  createUser: userData => UsersApi.post(`${useradmApiUrl}/users`, userData),

  removeUser: userId => UsersApi.delete(`${useradmApiUrl}/users/${userId}`),

  editUser: (userId, userData) => UsersApi.put(`${useradmApiUrl}/users/${userId}`, userData),

  setCurrentUser: user =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_CURRENT_USER,
      user: user
    }),

  /*
    Tenant management + Hosted Mender
  */
  getUserOrganization: () =>
    GeneralApi.get(`${tenantadmUrl}/user/tenant`).then(res => {
      AppDispatcher.handleViewAction({
        actionType: AppConstants.SET_ORGANIZATION,
        organization: res.body?.tenant_info?.organization
      });
      AppDispatcher.handleViewAction({
        actionType: AppConstants.SET_SPTS_STATUS,
        sptsStatus: res.body?.post_process?.filter(item => item.type === 'spts')[0].status
      });
      AppDispatcher.handleViewAction({
        actionType: AppConstants.SET_PROXY_STATUS,
        proxyStatus: res.body?.tenant_features?.filter(item => item.type === 'http_proxy')[0].status
      });
      AppDispatcher.handleViewAction({
        actionType: AppConstants.SET_ONE2M_STATUS,
        one2mStatus: res.body?.tenant_features?.filter(item => item.type === 'one2m')[0].status
      });
      return Promise.resolve(res.body);
    }),

  getHostedLinks: id => GeneralApi.getNoauth(`${hostedLinks}${id}/links.json`).then(res => JSON.parse(res.text)),

  /*
    Global settings
  */
  getGlobalSettings: () =>
    GeneralApi.get(`${useradmApiUrl}/settings`).then(res => {
      // AppDispatcher.handleViewAction({
      //   actionType: AppConstants.SET_GLOBAL_SETTINGS,
      //   settings: res.body
      // });
      return Promise.resolve(res.body);
    }),

  saveGlobalSettings: settings =>
    UsersApi.post(`${useradmApiUrl}/settings`, settings).then(() => {
      AppDispatcher.handleViewAction({
        actionType: AppConstants.SET_GLOBAL_SETTINGS,
        settings: settings
      });
      return Promise.resolve();
    }),

  /*
    Onboarding
  */
  setShowHelptips: val => {
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SHOW_HELP,
      show: val
    });
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SHOW_ONBOARDING_HELP,
      show: val
    });
  },
  setShowOnboardingHelp: val =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SHOW_ONBOARDING_HELP,
      show: val
    }),
  setOnboardingProgress: value =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_ONBOARDING_PROGRESS,
      value
    }),
  setOnboardingDeviceType: value =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_ONBOARDING_DEVICE_TYPE,
      value
    }),
  setShowDismissOnboardingTipsDialog: val =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SHOW_ONBOARDING_HELP_DIALOG,
      show: val
    }),
  setOnboardingComplete: val => {
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_ONBOARDING_COMPLETE,
      show: val
    });
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SHOW_ONBOARDING_HELP,
      show: !val
    });
    if (val) {
      advanceOnboarding('onboarding-finished');
    }
  },
  setShowConnectingDialog: val =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SHOW_CONNECT_DEVICE,
      show: val
    }),
  setShowCreateArtifactDialog: val =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_SHOW_CREATE_ARTIFACT,
      show: val
    }),
  setConnectingDialogProgressed: val => {
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_CONNECT_DEVICE_PROGRESSED,
      progressed: val
    });
    if (val) {
      advanceOnboarding('devices-accepted-onboarding');
    }
  },

  /* Artifacts */
  getArtifacts: () =>
    ArtifactsApi.get(`${deploymentsApiUrl}/artifacts`).then(artifacts => {
      var rels = [];
      artifacts.forEach(function (a) {
        if (a.updates[0].type_info.type === 'rootfs-image') {
          rels.push(a);
        }
      });

      artifacts = rels;

      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_ARTIFACTS,
        artifacts: artifacts
      });
      return Promise.resolve(artifacts);
    }),

  getAiPackages: () =>
    ArtifactsApi.get(`${aiDeploymentsApiUrl}/artifacts`).then(aipackages => {
      var aips = [];
      aipackages.forEach(function (aip) {
        if (aip.updates[0].type_info.type !== 'rootfs-image') {
          aips.push(aip);
        }
      });

      aipackages = aips;

      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_AIPACKAGES,
        aipackages: aipackages
      });
      return Promise.resolve(aipackages);
    }),

  getArtifactUrl: id =>
    ArtifactsApi.get(`${deploymentsApiUrl}/artifacts/${id}/download`).then(response => {
      AppDispatcher.handleViewAction({
        actionType: AppConstants.ARTIFACTS_SET_ARTIFACT_URL,
        id,
        url: response.uri
      });
      return Promise.resolve(response.uri);
    }),

  getAiArtifactUrl: id =>
    ArtifactsApi.get(`${aiDeploymentsApiUrl}/artifacts/${id}/download`).then(response => {
      AppDispatcher.handleViewAction({
        actionType: AppConstants.ARTIFACTS_SET_AI_ARTIFACT_URL,
        id,
        url: response.uri
      });
      return Promise.resolve(response.uri);
    }),

  uploadArtifact: (meta, file, progress) => {
    var formData = new FormData();
    formData.append('size', file.size);
    formData.append('description', meta.description);
    formData.append('artifact', file);
    AppDispatcher.handleViewAction({
      actionType: AppConstants.UPLOAD_PROGRESS,
      inprogress: true
    });
    return ArtifactsApi.postFormData(`${deploymentsApiUrl}/artifacts`, formData, e => progress(e.percent))
      .then(() => {
        AppDispatcher.handleViewAction({
          actionType: AppConstants.UPLOAD_ARTIFACT,
          artifact: file
        });
      })
      .finally(() =>
        AppDispatcher.handleViewAction({
          actionType: AppConstants.UPLOAD_PROGRESS,
          inprogress: false
        })
      );
  },

  uploadAiArtifact: (meta, file, progress) => {
    var formData = new FormData();
    formData.append('size', file.size);
    formData.append('description', meta.description);
    formData.append('artifact', file);
    AppDispatcher.handleViewAction({
      actionType: AppConstants.UPLOAD_PROGRESS,
      inprogress: true
    });
    return ArtifactsApi.postFormData(`${aiDeploymentsApiUrl}/artifacts`, formData, e => progress(e.percent))
      .then(() => {
        AppDispatcher.handleViewAction({
          actionType: AppConstants.UPLOAD_ARTIFACT,
          artifact: file
        });
      })
      .finally(() =>
        AppDispatcher.handleViewAction({
          actionType: AppConstants.UPLOAD_PROGRESS,
          inprogress: false
        })
      );
  },

  editArtifact: (id, body) => ArtifactsApi.putMultipart(`${deploymentsApiUrl}/artifacts/${id}`, body),

  editAiArtifact: (id, body) => ArtifactsApi.putJSON(`${aiDeploymentsApiUrl}/artifacts/${id}`, body),

  downloadArtifactReleaseNote: (id) => ArtifactsApi.download(`${deploymentsApiUrl}/releasenote/download/${id}`),

  deleteArtifactReleaseNote: (id) => ArtifactsApi.delete(`${deploymentsApiUrl}/releasenote/remove/${id}`),

  removeArtifact: id =>
    ArtifactsApi.delete(`${deploymentsApiUrl}/artifacts/${id}`).then(() =>
      AppDispatcher.handleViewAction({
        actionType: AppConstants.ARTIFACTS_REMOVED_ARTIFACT,
        id
      })
    ),

  removeAiArtifact: id =>
    ArtifactsApi.delete(`${aiDeploymentsApiUrl}/artifacts/${id}`).then(() =>
      AppDispatcher.handleViewAction({
        actionType: AppConstants.ARTIFACTS_REMOVED_AI_ARTIFACT,
        id
      })
    ),

  setDeploymentRelease: release =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_DEPLOYMENT_RELEASE,
      release
    }),

  /* Releases */
  getReleases: () =>
    ArtifactsApi.get(`${deploymentsApiUrl}/deployments/releases`).then(releases => {
      var rels = [];
      releases.forEach(function (rel) {
        if (rel.Artifacts.findIndex(function (a) {
          return (a.updates[0].type_info.type === 'rootfs-image');
        }) > -1) {
          rels.push(rel);
        }
      });

      releases = rels;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_RELEASES,
        releases
      });
      return Promise.resolve(rels);
    }),

  getContainers: () =>
    ArtifactsApi.get(`${aiDeploymentsApiUrl}/deployments/releases`).then(releases => {
      var containers = [];
      releases.forEach(function (rel) {
        if (rel.Artifacts.findIndex(function (a) {
          return (a.updates[0].type_info.type !== 'rootfs-image');
        }) > -1) {
          containers.push(rel);
        }
      });

      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_CONTAINERS,
        containers
      });
      return Promise.resolve(containers);
    }),

  /*Deployments */
  // all deployments
  getDeployments: (page = default_page, per_page = default_per_page) =>
    DeploymentsApi.get(`${deploymentsApiUrl}/deployments?page=${page}&per_page=${per_page}`).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_DEPLOYMENTS,
        deployments
      });
      return Promise.resolve(deployments);
    }),

  getAiDeployments: (page = default_page, per_page = default_per_page) =>
    DeploymentsApi.get(`${aiDeploymentsApiUrl}/deployments?page=${page}&per_page=${per_page}`).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_AI_DEPLOYMENTS,
        deployments
      });
      return Promise.resolve(deployments);
    }),

  getDeploymentsInProgress: (page = default_page, per_page = default_per_page) =>
    DeploymentsApi.get(`${deploymentsApiUrl}/deployments?status=inprogress&page=${page}&per_page=${per_page}`).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_ACTIVE_DEPLOYMENTS,
        deployments
      });
      return Promise.resolve(deployments);
    }),

  getAiDeploymentsInProgress: (page = default_page, per_page = default_per_page) =>
    DeploymentsApi.get(`${aiDeploymentsApiUrl}/deployments?status=inprogress&page=${page}&per_page=${per_page}`).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_ACTIVE_AI_DEPLOYMENTS,
        deployments
      });
      return Promise.resolve(deployments);
    }),

  getPastDeployments: (page = default_page, per_page = default_per_page, startDate, endDate, group) => {
    var created_after = startDate ? `&created_after=${startDate}` : '';
    var created_before = endDate ? `&created_before=${endDate}` : '';
    var search = group ? `&search=${group}` : '';

    return DeploymentsApi.get(
      `${deploymentsApiUrl}/deployments?status=finished&per_page=${per_page}&page=${page}${created_after}${created_before}${search}`
    ).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_PAST_DEPLOYMENTS,
        deployments
      });
      return Promise.resolve(deployments);
    });
  },

  getPastAiDeployments: (page = default_page, per_page = default_per_page, startDate, endDate, group) => {
    var created_after = startDate ? `&created_after=${startDate}` : '';
    var created_before = endDate ? `&created_before=${endDate}` : '';
    var search = group ? `&search=${group}` : '';

    return DeploymentsApi.get(
      `${aiDeploymentsApiUrl}/deployments?status=finished&per_page=${per_page}&page=${page}${created_after}${created_before}${search}`
    ).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_PAST_AI_DEPLOYMENTS,
        deployments
      });
      return Promise.resolve(deployments);
    });
  },

  getPendingDeployments: (page = default_page, per_page = default_per_page) =>
    DeploymentsApi.get(`${deploymentsApiUrl}/deployments?status=pending&page=${page}&per_page=${per_page}`).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_PENDING_DEPLOYMENTS,
        deployments: deployments
      });
      var links = parse(res.headers['link']);
      return Promise.resolve({ deployments, links });
    }),

  getPendingAiDeployments: (page = default_page, per_page = default_per_page) =>
    DeploymentsApi.get(`${aiDeploymentsApiUrl}/deployments?status=pending&page=${page}&per_page=${per_page}`).then(res => {
      var deployments = res.body;
      AppDispatcher.handleViewAction({
        actionType: AppConstants.RECEIVE_PENDING_AI_DEPLOYMENTS,
        deployments: deployments
      });
      var links = parse(res.headers['link']);
      return Promise.resolve({ deployments, links });
    }),

  getDeploymentCount: (status, startDate, endDate, group) => {
    var created_after = startDate ? `&created_after=${startDate}` : '';
    var created_before = endDate ? `&created_before=${endDate}` : '';
    var search = group ? `&search=${group}` : '';
    const DeploymentCount = (page = 1, per_page = 500, count = 0) =>
      DeploymentsApi.get(`${deploymentsApiUrl}/deployments?status=${status}&per_page=${per_page}&page=${page}${created_after}${created_before}${search}`).then(
        res => {
          var links = parse(res.headers['link']);
          count += res.body.length;
          if (links.next) {
            page++;
            return DeploymentCount(page, per_page, count);
          }
          return Promise.resolve(count);
        }
      );

    return DeploymentCount().then(count => {
      if (status === 'inprogress') {
        // AppDispatcher.handleViewAction({
        //   actionType: AppConstants.INPROGRESS_COUNT,
        //   count: count
        // });
      }
      return Promise.resolve(count);
    });
  },

  getAiDeploymentCount: (status, startDate, endDate, group) => {
    var created_after = startDate ? `&created_after=${startDate}` : '';
    var created_before = endDate ? `&created_before=${endDate}` : '';
    var search = group ? `&search=${group}` : '';
    const DeploymentCount = (page = 1, per_page = 500, count = 0) =>
      DeploymentsApi.get(`${aiDeploymentsApiUrl}/deployments?status=${status}&per_page=${per_page}&page=${page}${created_after}${created_before}${search}`).then(
        res => {
          var links = parse(res.headers['link']);
          count += res.body.length;
          if (links.next) {
            page++;
            return DeploymentCount(page, per_page, count);
          }
          return Promise.resolve(count);
        }
      );

    return DeploymentCount().then(count => {
      if (status === 'inprogress') {
        AppDispatcher.handleViewAction({
          actionType: AppConstants.INPROGRESS_COUNT,
          count: count
        });
      }
      return Promise.resolve(count);
    });
  },

  createDeployment: deployment => DeploymentsApi.post(`${deploymentsApiUrl}/deployments`, deployment).then(data => data.location),

  getSingleDeployment: id => DeploymentsApi.get(`${deploymentsApiUrl}/deployments/${id}`).then(res => res.body),

  getSingleDeploymentStats: id => DeploymentsApi.get(`${deploymentsApiUrl}/deployments/${id}/statistics`).then(res => res.body),

  getSingleDeploymentDevices: id => DeploymentsApi.get(`${deploymentsApiUrl}/deployments/${id}/devices`).then(res => res.body),

  getDeviceLog: (deploymentId, deviceId) => DeploymentsApi.getText(`${deploymentsApiUrl}/deployments/${deploymentId}/devices/${deviceId}/log`),

  abortDeployment: deploymentId => DeploymentsApi.put(`${deploymentsApiUrl}/deployments/${deploymentId}/status`, { status: 'aborted' }),

  createAiDeployment: deployment => DeploymentsApi.post(`${aiDeploymentsApiUrl}/deployments`, deployment).then(data => data.location),

  getSingleAiDeployment: id => DeploymentsApi.get(`${aiDeploymentsApiUrl}/deployments/${id}`).then(res => res.body),

  getSingleAiDeploymentStats: id => DeploymentsApi.get(`${aiDeploymentsApiUrl}/deployments/${id}/statistics`).then(res => res.body),

  getSingleAiDeploymentDevices: id => DeploymentsApi.get(`${aiDeploymentsApiUrl}/deployments/${id}/devices`).then(res => res.body),

  getAiDeviceLog: (deploymentId, deviceId) => DeploymentsApi.getText(`${aiDeploymentsApiUrl}/deployments/${deploymentId}/devices/${deviceId}/log`),

  abortAiDeployment: deploymentId => DeploymentsApi.put(`${aiDeploymentsApiUrl}/deployments/${deploymentId}/status`, { status: 'aborted' }),

  sortTable: (table, column, direction) =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SORT_TABLE,
      table: table,
      column: column,
      direction: direction
    }),

  setLocalStorage: (key, value) =>
    AppDispatcher.handleViewAction({
      actionType: AppConstants.SET_LOCAL_STORAGE,
      key: key,
      value: value
    }),

  /* APIs for Local Configuration */
  getLocalConfigById: id => DevicesApi.get(`${localConfigurationUrl}/${id}`).then(res => res.body),
  getLocalConfigByDeviceUUID: (uuid, selectors) => {
    let url = '';
    if (selectors === '') {
      url = `${localConfigurationUrl}/devices/${uuid}`;
    } else {
      url = `${localConfigurationUrl}/devices/${uuid}?fields=${selectors}`;
    }
    return DevicesApi.get(url).then(res => res.body);
  },
  getAllLocalConfs: () => {
    const getAllLocalConfs = (per_page = 200, page = 1, lconfs = []) =>
      DevicesApi.get(`${localConfigurationUrl}/list?per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        lconfs.push(...res.body);
        if (links.next) {
          return getAllLocalConfs(per_page, page + 1, lconfs);
        }
        return Promise.resolve(lconfs);
      });
    return getAllLocalConfs();
  },

  patchLocalConfigById: (id, config) => DevicesApi.patch(`${localConfigurationUrl}_requests/${id}`, config),

  //MP TODO: More Local Config API to follow

  //
  /* APIs for Configuration */
  getConfigurationCount: (configType, callback) => {
    DevicesApi.get(configurationApiUrl + '/' + configType + configurationCount)
      .then(function (res) {
        callback.success(res.body.count);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  //configType - Config or Flavor
  //type - VMS or VSAAS
  getConfigsByType: (configType, type) => DevicesApi.get(configurationApiUrl + '/' + configType + configurationList + '/type/' + type).then(res => res.body),

  getConfigurationsByType: (configType, type, callback) => {
    //configType - Config or Flavor
    //type - VMS or VSAAS
    DevicesApi.get(
      configurationApiUrl +
      '/' +
      configType +
      configurationList +
      '/type/' +
      type
    )
      .then(function (res) {
        callback.success(res.body);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  getConfigurations: (configType, callback, pages, per_pages, group, search_term) => {
    var page = pages || default_page;
    var per_page = per_pages || default_per_page;
    var forGroup = group ? '&group=' + group : '';
    var searchTerm = search_term ? '&' + search_term : '';
    var type = configType;
    DevicesApi.get(
      configurationApiUrl +
      '/' +
      type +
      configurationList +
      '?per_page=' +
      per_page +
      '&page=' +
      page +
      searchTerm +
      forGroup
    )
      .then(function (res) {
        callback.success(res.body);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  assignAiPackage: (deviceId, aiPackage) => {
    return DevicesApi.put(configurationApiUrl + '/configmaps/device' + '/' + deviceId, aiPackage)
  },

  getDeviceConfiguration: (deviceId, callback) => {
    DevicesApi.get(configurationApiUrl + '/configmaps' + '/' + deviceId)
      .then(function (res) {
        callback.success(res.body);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  createConfiguration: (configType, configData, callback) => {
    DevicesApi.post(
      configurationApiUrl + '/' + configType + '/list',
      configData
    )
      .then(function (res) {
        callback.success(res);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  editConfiguration: (configType, devConfId, devConfData, callback) => {
    DevicesApi.put(
      configurationApiUrl + '/' + configType + '/' + devConfId,
      devConfData
    )
      .then(function (res) {
        callback.success(res);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  deleteConfiguration: (configType, devConfId, callback) => {
    DevicesApi.delete(configurationApiUrl + '/' + configType + '/' + devConfId)
      .then(function (res) {
        callback.success(res);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  getConfigById: id => DevicesApi.get(`${configurationApiUrl}/configmaps/${id}`).then(res => res.body),

  /* Action APIs */
  getActionByDeviceId: id => DevicesApi.get(`${actionApiUrl}/${id}`).then(res => res.body),

  updateActionByDeviceId: (id, actions, callback) => {
    DevicesApi.put(`${actionApiUrl}/${id}`, actions)
      .then(function (res) {
        callback.success(res);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  createActionByDeviceId: (id, actions, callback) => {
    DevicesApi.post(`${actionApiUrl}/${id}`, actions)
      .then(function (res) {
        callback.success(res);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  getActions: (callback, pages, per_pages) => {
    var page = pages || default_page;
    var per_page = per_pages || default_per_page;
    DevicesApi.get(
      `${actionApiUrl}/list` +
      '?per_page=' +
      per_page +
      '&page=' +
      page
    )
      .then(function (res) {
        callback.success(res.body);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  getAllActions: () => {
    const getAllActions = (per_page = 200, page = 1, actions = []) =>
      DevicesApi.get(`${actionApiUrl}/list?per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        actions.push(...res.body);
        if (links.next) {
          return getAllActions(per_page, page + 1, actions);
        }
        return Promise.resolve(actions);
      });
    return getAllActions();
  },

  getHeartBeatFromTsdb: (deviceId, time) => {
    let url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('devices')}`;
    return DevicesApi.postInfluxDB(url, `q=SELECT "state" FROM "heartbeat" WHERE "device_id"='${deviceId}' AND time > now() - ${time}`);
  },

  getAlarmsFromTsdb: (deviceId, time) => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('actions')}`;
    return DevicesApi.postInfluxDB(url, `q=SELECT "alarms" FROM "action" WHERE "device_id"='${deviceId}' AND "alarms" != 'null' AND time > now() - ${time}`);
  },

  getActionsFromTsdb: (deviceId, time) => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('actions')}`;
    return DevicesApi.postInfluxDB(url, `q=SELECT * FROM "action" WHERE "device_id"='${deviceId}' AND "alarms" = 'null' AND time > now() - ${time}`);
  },

  getLConfFromTsdb: (deviceId, time) => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('localconfs')}`;
    return DevicesApi.postInfluxDB(url, `q=SELECT * FROM "lconf" WHERE "device_id"='${deviceId}' AND time > now() - ${time}`);
  },

  getConfigmapFromTsdb: (deviceId, time) => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('configurations')}`;
    return DevicesApi.postInfluxDB(url, `q=SELECT * FROM "configmap" WHERE "device_id"='${deviceId}' AND time > now() - ${time}`);
  },

  getTsdbDataInfo: ({ db, measurement, time, searchKey, searchValue }) => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy(db)}`;
    return DevicesApi.postInfluxDB(url, `q=SELECT * FROM "${measurement}" WHERE "${searchKey}"='${searchValue}' AND time > now() - ${time}`);
  },

  /* tenant management */
  createTenantDeviceMap: (mapData) => {
    return DevicesApi.post(tenantadmUrl + '/tdmap', mapData, true);
  },

  uploadTenantDeviceMap: (mapData) => {
    return DevicesApi.post(`${tenantadmUrl}/map`, mapData);
  },

  editTenantDeviceMap: (mapId, mapData) => {
    return DevicesApi.put(`${tenantadmUrl}/tdmap/${mapId}`, mapData);
  },

  getTenantDeviceMapCount: () => {
    return DevicesApi.get(`${tenantadmUrl}/mapcount`);
  },

  addDeviceToTenant: (uuid) => {
    return DevicesApi.post(`${tenantadmUrl}/tdmap/devices/${uuid}`);
  },

  getTenantDeviceMap: (page, per_page, group, search_term) => {
    page = page || default_page;
    per_page = per_page || default_per_page;
    var forGroup = group ? '&group=' + group : '';
    var searchTerm = search_term ? '&' + search_term : '';
    return DevicesApi.get(`${tenantadmUrl}/map?per_page=${per_page}&page=${page}${searchTerm}${forGroup}`);
  },

  getAllTenantDeviceMap: () => {
    const getAllTenantDeviceMap = (per_page = 200, page = 1, deviceMap = []) =>
      DevicesApi.get(`${tenantadmUrl}/map?per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        deviceMap.push(...res.body);
        if (links.next) {
          return getAllTenantDeviceMap(per_page, page + 1, deviceMap);
        }
        return Promise.resolve(deviceMap);
      });
    return getAllTenantDeviceMap();
  },

  deleteTenantDeviceMap: (mapId) => {
    return DevicesApi.delete(`${tenantadmUrl}/tdmap/${mapId}`);
  },

  createTenant: (tenantData) => {
    return DevicesApi.post(`${tenantadmUrl}/tenants`, tenantData, true);
  },

  getTenantsCount: () => {
    return DevicesApi.get(`${tenantadmUrl}/tenants/count`);
  },

  getTenants: (page, per_page, group, search_term) => {
    page = page || default_page;
    per_page = per_page || default_per_page;
    const forGroup = group ? '&group=' + group : '';
    const searchTerm = search_term ? '&' + search_term : '';
    return DevicesApi.get(`${tenantadmUrl}/tenants?per_page=${per_page}&page=${page}${searchTerm}${forGroup}`);
  },

  getAllTenants: () => {
    const getAllTenants = (per_page = 200, page = 1, tenants = []) =>
      DevicesApi.get(`${tenantadmUrl}/tenants?per_page=${per_page}&page=${page}`)
        .then(res => {
          let links = parse(res.headers['link']);
          tenants.push(...res.body);
          if(links.next)
            return getAllTenants(per_page, page + 1, tenants);
          return Promise.resolve(tenants);
        })
    return getAllTenants();
  },

  getTenantById: function (id, callback) {
    DevicesApi
      .get(tenantadmUrl + '/tenants/' + id)
      .then(function (res) {
        callback.success(res.body);
      })
      .catch(function (err) {
        callback.error(err);
      });
  },

  editTenant: (TenantId, TenantData) => {
    return DevicesApi.put(tenantadmUrl + '/tenants/' + TenantId, TenantData);
  },

  deleteTenant: (TenantId) => {
    return DevicesApi.delete(tenantadmUrl + '/tenants/' + TenantId);
  },

  createTenantUser: (tenantId, userData) => {
    return DevicesApi.post(`${tenantadmUrl}/tenants/${tenantId}/users`, userData);
  },

  getTenantUserList: (tenantId) => {
    return DevicesApi.get(`${tenantadmUrl}/tenants/${tenantId}/users`);
  },

  getTenantEwcConfig: (tenantId) => {
    return DevicesApi.get(`${tenantadmUrl}/tenants/${tenantId}/download/ewxconf`);
  },

  createOrganizationTrial: (data) => {
    return DevicesApi.post(`${tenantadmUrlV2}/tenants/trial`, data);
  },

  editTenantFeatures: (tenantId, data) => {
    return DevicesApi.put(`${tenantadmUrlV2}/tenants/${tenantId}/postprocess/features`, data)
  },

  getDevicesHistory: (pageNo = 1, pageLength = 20) => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('dhistory')}`;
    return DevicesApi.postInfluxDB(url, `q=SELECT LAST("status") FROM "history" GROUP BY "uuid" SLIMIT ${pageLength} SOFFSET ${(pageNo - 1) * pageLength}`);
  },

  getDeviceHistoryCount: () => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('dhistory')}`;
    return DevicesApi.postInfluxDB(url, `q=SHOW SERIES`);
  },

  getDeviceHistroyDetails: (uuid = '') => {
    const url = `${influxdbUrl}/query?db=${convertDbToMultiTenancy('dhistory')}&epoch=ms`;
    return DevicesApi.postInfluxDB(url, `q=SELECT time, status FROM "history" WHERE "uuid"='${uuid}' ORDER BY DESC`);
  },

  getPreferences: () => {
    return PreferencesApi.get(`${preferencesUrl}/servers`);
  },

  createPreference: (preference) => {
    return PreferencesApi.post(`${preferencesUrl}/servers`, preference, true);
  },

  deletePreference: (id) => {
    return PreferencesApi.delete(`${preferencesUrl}/servers/${id}`);
  },

  deletePrefernceAttr: (id, keys) => {
    return PreferencesApi.delete(`${preferencesUrl}/servers/${id}?keys=${keys}`);
  },

  updatePrefernceAttr: (id, pref) => {
    return PreferencesApi.patch(`${preferencesUrl}/servers/${id}`, pref, true);
  },

  getInfrastructureCount: () => {
    return InfraStructureApi.get(`${infrastructureUrl}/servers/count`);
  },

  getInfraStructureById: (id) => {
    return InfraStructureApi.get(`${infrastructureUrl}/${id}`);
  },

  getInfraStructuresAll: () => {
    const getInfraStructuresAll = (per_page = 200, page = 1, infrastructures = []) =>
      InfraStructureApi.get(`${infrastructureUrl}?per_page=${per_page}&page=${page}`).then(response => {
        let links = parse(response.headers['link']);
        infrastructures.push(...response.body);
        if(links.next)
          return getInfraStructuresAll(per_page, page + 1, infrastructures);
        return Promise.resolve(infrastructures);
      });
    return getInfraStructuresAll();
  },

  getInfraByUuid: uuid => {
    const getInfraByUuid = (per_page = 200, page = 1, infrastructures = []) =>
      InfraStructureApi.get(`${infrastructureUrl}?nodes=${uuid}&page=${page}&per_page=${per_page}`).then(response => {
        let links = parse(response.headers['link']);
        infrastructures.push(...response.body);
        if(links.next)
          return getInfraByUuid(per_page, page + 1, infrastructures);
        return Promise.resolve(infrastructures);
      });
    return getInfraByUuid();
  },

  getInfraByRegion: region => InfraStructureApi.get(`${infrastructureUrl}?region=${region}`),

  getInfraStructureAllWithSecificFields: (fNames) => {
    return InfraStructureApi.get(`${infrastructureUrl}?fields=${fNames}`)
  },

  getInfraStructureByPairingStatus: status => {
    return InfraStructureApi.get(`${infrastructureUrl}?pairing_status=${status}`);
  },

  getInfraStructures: (currentPage, per_page) => {
    return InfraStructureApi.get(`${infrastructureUrl}?per_page=${per_page}&page=${currentPage}`);
  },

  createInfrastructure: (data) => {
    return InfraStructureApi.post(`${infrastructureUrl}`, data);
  },

  updateInfrastructure: (id, data) => {
    return InfraStructureApi.put(`${infrastructureUrl}/${id}`, data);
  },

  removeInfrastructure: id => {
    return InfraStructureApi.delete(`${infrastructureUrl}/${id}?confirm=true`);
  },

  setDefaultInfrastructure: id => {
    return InfraStructureApi.put(`${infrastructureUrl}/${id}/default`);
  },

  updateInfrastructurePort: (id, data) => {
    return InfraStructureApi.put(`${infrastructureUrl}/${id}/ports`, data);
  },

  getAllVC: () => {
    const getAllVC = (per_page = 200, page = 1, vcs = []) =>
      InfraStructureApi.get(`${vcamlogicUrlV2}/vcams?per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        vcs.push(...res.body);
        if (links.next) {
          return getAllVC(per_page, page + 1, vcs);
        }
        return Promise.resolve(vcs);
      });
    return getAllVC();
  },

  getVcByInfraName: name => {
    const getVcByInfraName = (per_page = 200, page = 1, vcs = []) =>
      InfraStructureApi.get(`${vcamlogicUrlV2}/vcams?ewc_instance_name=${name}&per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        vcs.push(...res.body);
        if (links.next) {
          return getVcByInfraName(per_page, page + 1, vcs);
        }
        return Promise.resolve(vcs);
      });
    return getVcByInfraName();
  },

  getVcByUuid: uuid => InfraStructureApi.get(`${vcamlogicUrlV2}/vcams?device_uuid=${uuid}`),

  updateVcamType: (uuid, type) => InfraStructureApi.put(`${vcamlogicUrlV2}/vcams/${uuid}`, { change: 'type', value: type }),

  updateVcamEwc:(uuid, ewcName) => InfraStructureApi.put(`${vcamlogicUrlV2}/vcams/${uuid}`, { change: 'ewc', value: ewcName }),

  restartVcam: uuid => InfraStructureApi.put(`${vcamlogicUrlV2}/vcams/${uuid}`, { change: 'state', value: 'restart' }),

  getDevicesAll: () =>{
    return DevicesApi.get(`${vcamlogicUrl}/devices`);
  },

  getVcamList: deviceId => {
    return DevicesApi.get(`${vcamlogicUrl}/devices/${deviceId}`);
  },

  postVcamRestartOrStop: (deviceId, data) => {
    return DevicesApi.put(`${vcamlogicUrl}/devices/${deviceId}/vcams/commands`, data);
  },

  getEwcServerDetails: ip => {
    return InfraStructureApi.get(`${infrastructureUrl}/servers/status?host=${ip}`);
  },

  getTenantEwcServerStatus: tenantId => {
    return InfraStructureApi.get(`${infrastructureUrl}/tenants/${tenantId}/status`);
  },

  getTenantEwmEwcPairingStatus: tenantId => {
    return InfraStructureApi.get(`${infrastructureUrl}/tenants/${tenantId}/pair`);
  },

  veifyTenantNameAvailability: tenantName => DevicesApi.post(`${tenantadmUrlV2}/validate/tenants/${tenantName}`),

  verifyUuidAvailability: uuid => DevicesApi.post(`${tenantadmUrlV2}/validate/devices/${uuid}`),

  postVcamType: (deviceId, mac, idx, type) => DevicesApi.put(`${vcamlogicUrl}/devices/${deviceId}/camera/${mac}/slots/${idx}/type`, {type: type}),

  getAllCameras: () => {
    const getAllCameras = (per_page = 200, page = 1, pcams = []) =>
      CamerasApi.get(`${vcamlogicUrlV2}/pcams?per_page=${per_page}&page=${page}`).then(res => {
        var links = parse(res.headers['link']);
        pcams.push(...res.body);
        if (links.next) {
          return getAllCameras(per_page, page + 1, pcams);
        }
        return Promise.resolve(pcams);
      });
    return getAllCameras();
  },

  removeCameras: id => CamerasApi.delete(`${vcamlogicUrlV2}/pcams/${id}`),

  exportConfig: () => CamerasApi.exportConfig(`${vcamlogicUrlV2}/pp/spts`),

  importConfig: (config, progress) =>
    CamerasApi.importConfig(`${vcamlogicUrlV2}/pp/spts`, config, e => progress(e.percent)),
  
  getSptsStatus: () => CamerasApi.get(`${vcamlogicUrlV2}/pp/spts/status`),

  getCamerasCount: (isConfigured) => CamerasApi.get(`${vcamlogicUrlV2}/pp/spts/count?configured=${isConfigured}`),

  getListOfNotifications: () => EventsAndMetricsApi.get(`${notificationMgr}/notifications`),

  subscribeToEventsAndMetrics: () => EventsAndMetricsApi.get(`${notificationMgr}/subscription`),

  removeNotifications: () => EventsAndMetricsApi.delete(`${notificationMgr}/notifications`),

  removeNotificationsGrouped: (params) => {
    const {type = '', olderThan = ''} = params;
    const typeParam = `type=${type}`, olderThanParam = `older-than=${olderThan}`;
    const requestParams = () => {
      if(type !== '' && olderThan !== '')
        return `${typeParam}&${olderThanParam}`;
      else if(type === '')
        return olderThanParam;
      else
        return typeParam;
    };
    console.log(`\n\nurl -> ${notificationMgr}/notifications?${requestParams()}\n\n`)
    return EventsAndMetricsApi.delete(`${notificationMgr}/notifications?${requestParams()}`)
  },

  restartPP: (uuid, type) => InfraStructureApi.put(`${vcamlogicUrlV2}/vcams/${uuid}/pp/${type}?restart=true`),

  getProxyInfo: () => InfraStructureApi.get(`${apiUrlV2}/infra_manager/settings/proxy`),

  updateFirstProxyInfo: (data) => InfraStructureApi.post(`${apiUrlV2}/infra_manager/settings/proxy`, data),

  updateSecondsProxyInfo: (data) => InfraStructureApi.put(`${apiUrlV2}/infra_manager/settings/proxy`, data),

  updateProxyStatus: (id, data) => DevicesApi.put(`${tenantadmUrlV2}/tenants/${id}/features`, data),

  duplicateVCam: (uuid) => InfraStructureApi.post(`${vcamlogicUrlV2}/vcams?primary=${uuid}`),

  deleteDuplicateVCam: (uuid) => InfraStructureApi.delete(`${vcamlogicUrlV2}/vcams/${uuid}`),

  getVCamsFromLink: (params) => InfraStructureApi.get(`${vcamlogicUrlV2}/vcams?${params}`)

}

export default AppActions;
